/**
 * GettingStartedCard — the onboarding checklist that sits at the top of the
 * Dashboard and becomes the hero of the zero-data dashboard.
 *
 * Behaviour (design proposal §3.2):
 * - Five verb-first items, each deep-linked; first-data opens the manual entry
 *   modal directly (not the chat sidebar).
 * - Dismiss collapses it to a compact "Setup n/5" pill that re-expands on click.
 * - When everything is done, a one-time celebration/graduation row points to
 *   Analysis, then the card removes itself permanently.
 * - Renders nothing until every constituent query has settled (`isReady`) so
 *   completion state never flashes in from a loading placeholder.
 * - With no tank, the create-tank row is the primary (and only actionable) CTA.
 */
import {
  Paper,
  Stack,
  Group,
  Text,
  Progress,
  Button,
  ThemeIcon,
  UnstyledButton,
  ActionIcon,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCircleCheck,
  IconCircle,
  IconChevronRight,
  IconX,
  IconSparkles,
  IconRocket,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { useGettingStarted, type GettingStartedItem } from '@/hooks/useGettingStarted';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal';

const HEADING_FONT = { fontFamily: 'Space Grotesk, sans-serif' };

interface ChecklistRowProps {
  item: GettingStartedItem;
  /** When true (no tank yet), this row is styled as the primary next action. */
  primary: boolean;
  onOpenEntry: () => void;
}

function RowInner({ item, primary }: { item: GettingStartedItem; primary: boolean }) {
  return (
    <Group gap="sm" wrap="nowrap" w="100%">
      <ThemeIcon
        variant={item.done ? 'light' : 'transparent'}
        color={item.done ? 'teal' : 'gray'}
        size="md"
        radius="xl"
      >
        {item.done ? <IconCircleCheck size={20} /> : <IconCircle size={20} />}
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text
          size="sm"
          fw={primary ? 600 : 500}
          td={item.done ? 'line-through' : undefined}
          c={item.done ? 'dimmed' : undefined}
        >
          {item.label}
        </Text>
        <Text size="xs" c="dimmed">
          {item.description}
        </Text>
      </Box>
      {!item.done && <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />}
    </Group>
  );
}

function ChecklistRow({ item, primary, onOpenEntry }: ChecklistRowProps) {
  const sharedStyle = {
    root: {
      borderRadius: 'var(--mantine-radius-md)',
      border: primary
        ? '1px solid var(--mantine-primary-color-filled)'
        : '1px solid var(--mantine-color-default-border)',
      padding: '10px 12px',
      backgroundColor: primary ? 'var(--mantine-primary-color-light)' : undefined,
    },
  };

  if (item.action === 'open-entry') {
    return (
      <UnstyledButton onClick={onOpenEntry} styles={sharedStyle}>
        <RowInner item={item} primary={primary} />
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton
      component={Link}
      to={item.to ?? '#'}
      onClick={() => item.onSelect?.()}
      styles={sharedStyle}
    >
      <RowInner item={item} primary={primary} />
    </UnstyledButton>
  );
}

export function GettingStartedCard() {
  const { tanks } = useTank();
  const {
    items,
    completedCount,
    allDone,
    isReady,
    showCelebration,
    dismissed,
    dismiss,
    restore,
  } = useGettingStarted();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Render nothing until every constituent query has settled so completion
  // state never flashes in from a loading placeholder.
  if (!isReady) return null;

  const total = items.length;

  // Everything done: show the one-time celebration, then nothing forever.
  if (allDone) {
    if (!showCelebration) return null;
    return (
      <Paper
        withBorder
        radius="xl"
        p={{ base: 'sm', md: 'lg' }}
        data-testid="getting-started-celebration"
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon variant="light" color="teal" size="lg" radius="xl">
              <IconSparkles size={22} />
            </ThemeIcon>
            <Box>
              <Text fw={600} style={HEADING_FONT}>
                Your first chart is live — take a look
              </Text>
              <Text size="sm" c="dimmed">
                You have finished setting up. Head to Analysis to explore your data.
              </Text>
            </Box>
          </Group>
          <Button
            component={Link}
            to="/analysis"
            variant="light"
            color="teal"
            rightSection={<IconChevronRight size={16} />}
          >
            Take a look
          </Button>
        </Group>
      </Paper>
    );
  }

  // Dismissed: compact "Setup n/5" pill that re-expands on click.
  if (dismissed) {
    return (
      <Group>
        <Button
          variant="light"
          color="teal"
          size="xs"
          radius="xl"
          leftSection={<IconRocket size={14} />}
          onClick={restore}
        >
          {`Setup ${completedCount}/${total}`}
        </Button>
      </Group>
    );
  }

  const hasTank = tanks.length > 0;

  return (
    <>
      <Paper
        withBorder
        radius="xl"
        p={{ base: 'sm', md: 'lg' }}
        data-testid="getting-started-card"
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="teal" size="lg" radius="xl">
                <IconRocket size={22} />
              </ThemeIcon>
              <Box>
                <Text fw={600} style={{ ...HEADING_FONT, fontSize: 18 }}>
                  Getting started
                </Text>
                <Text size="sm" c="dimmed">
                  {hasTank
                    ? 'A few quick steps to get the most out of Reef DataHub.'
                    : 'Create your first tank to start tracking — everything begins here.'}
                </Text>
              </Box>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Dismiss getting started checklist"
              onClick={dismiss}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>

          <Box>
            <Progress
              value={(completedCount / total) * 100}
              color="teal"
              radius="xl"
              size="sm"
              aria-label="Getting started progress"
            />
            <Text size="xs" c="dimmed" mt={4}>
              {`${completedCount} of ${total} complete`}
            </Text>
          </Box>

          <Stack gap="xs">
            {items.map((item) => (
              <ChecklistRow
                key={item.key}
                item={item}
                primary={!hasTank && item.key === 'create-tank'}
                onOpenEntry={openModal}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <UnifiedManualEntryModal
        opened={modalOpened}
        onClose={closeModal}
        initialType="water-test"
      />
    </>
  );
}

export default GettingStartedCard;
